import { useState } from 'react';
import { FolderInput, Archive, ArchiveRestore, Trash2, RotateCcw, Flame, X, Folder, FolderMinus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useProjectsHubStore } from '../../stores/useProjectsHubStore';

export const BulkActionsBar = ({ view, onMove, onAct }) => {
  const { checked, clearChecked } = useProjectsHubStore();
  if (!checked.length) return null;
  const n = checked.length;
  const Btn = ({ icon: Icon, label, onClick, danger, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12.5px] font-medium transition-colors ${danger ? 'text-red-600 hover:bg-red-50' : 'text-[var(--c-text)] hover:bg-[var(--c-surface-2)]'}`}>
      <Icon className="w-3.5 h-3.5" />{label}
    </button>
  );
  return (
    <div className="sticky bottom-4 z-30 flex justify-center pointer-events-none" data-testid="bulk-actions-bar">
      <div className="pointer-events-auto flex items-center gap-1 pl-4 pr-2 h-12 rounded-2xl bg-[var(--c-surface-0)] border border-[var(--c-border-strong)]" style={{ boxShadow: '0 12px 32px rgba(0,0,0,0.12)' }}>
        <span className="text-[12.5px] text-[var(--c-text-2)] mr-2 tabular-nums"><b className="text-[var(--c-text)]">{n}</b> projet{n > 1 ? 's' : ''} sélectionné{n > 1 ? 's' : ''}</span>
        {view === 'trash' ? (
          <>
            <Btn icon={RotateCcw} label="Restaurer" onClick={() => onAct(checked, 'restore')} testid="bulk-restore-btn" />
            <Btn icon={Flame} label="Supprimer définitivement" onClick={() => onAct(checked, 'delete')} danger testid="bulk-delete-btn" />
          </>
        ) : (
          <>
            <Btn icon={FolderInput} label="Déplacer" onClick={() => onMove(checked)} testid="bulk-move-btn" />
            {view === 'archived'
              ? <Btn icon={ArchiveRestore} label="Désarchiver" onClick={() => onAct(checked, 'unarchive')} testid="bulk-unarchive-btn" />
              : <Btn icon={Archive} label="Archiver" onClick={() => onAct(checked, 'archive')} testid="bulk-archive-btn" />}
            <Btn icon={Trash2} label="Corbeille" onClick={() => onAct(checked, 'trash')} danger testid="bulk-trash-btn" />
          </>
        )}
        <button type="button" onClick={clearChecked} className="ml-1 w-8 h-8 rounded-lg flex items-center justify-center text-[var(--c-text-muted)] hover:bg-[var(--c-surface-2)]" aria-label="Annuler la sélection" data-testid="bulk-clear-btn"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const RenameProjectDialog = ({ project, onClose }) => {
  const renameProject = useProjectsHubStore((s) => s.renameProject);
  const [form, setForm] = useState({ name: project?.name || '', description: project?.description || '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try { await renameProject(project.project_id, form); onClose(true); } catch { onClose(false, 'Erreur lors de la modification'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Renommer le projet</DialogTitle><DialogDescription>Le nom et la description apparaissent dans la liste et les exports.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          <div><label className="text-xs font-medium text-[var(--c-text-2)] block mb-1.5">Nom *</label><Input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && submit()} data-testid="edit-project-name-input" /></div>
          <div><label className="text-xs font-medium text-[var(--c-text-2)] block mb-1.5">Description</label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-project-description-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()}>Annuler</Button>
          <button type="button" onClick={submit} disabled={busy || !form.name.trim()} className="hud-btn hud-btn-primary hud-btn-md" data-testid="confirm-edit-btn">Enregistrer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MoveToFolderDialog = ({ ids, onClose }) => {
  const { folders, organize, createFolder } = useProjectsHubStore();
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const move = async (folderId) => {
    setBusy(true);
    try { await organize(ids, 'move', folderId); onClose(true); } catch { onClose(false, 'Déplacement impossible'); } finally { setBusy(false); }
  };
  const createAndMove = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try { const f = await createFolder(name); await organize(ids, 'move', f.folder_id); onClose(true); } catch { onClose(false, 'Création impossible'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ids} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Déplacer {ids?.length > 1 ? `${ids.length} projets` : 'le projet'}</DialogTitle><DialogDescription>Choisissez un dossier de destination.</DialogDescription></DialogHeader>
        <div className="space-y-1 py-1 max-h-72 overflow-y-auto" data-testid="move-folder-list">
          <button type="button" disabled={busy} onClick={() => move(null)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-[var(--c-text-2)] hover:bg-[var(--c-surface-2)]" data-testid="move-folder-none"><FolderMinus className="w-4 h-4" />Aucun dossier</button>
          {folders.map((f) => (
            <button key={f.folder_id} type="button" disabled={busy} onClick={() => move(f.folder_id)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-[var(--c-text)] hover:bg-[var(--c-gold-soft)]" data-testid={`move-folder-${f.folder_id}`}>
              <Folder className="w-4 h-4 text-[var(--c-gold)]" /><span className="flex-1 text-left truncate">{f.name}</span><span className="text-[11px] text-[var(--c-text-faint)] tabular-nums">{f.count}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-[var(--c-border)]">
          <Input placeholder="Nouveau dossier…" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createAndMove()} data-testid="move-new-folder-input" />
          <button type="button" onClick={createAndMove} disabled={busy || !newName.trim()} className="hud-btn hud-btn-secondary hud-btn-sm shrink-0" data-testid="move-new-folder-btn">Créer</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ConfirmDeleteDialog = ({ ids, onClose, onConfirm }) => (
  <Dialog open={!!ids} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Supprimer définitivement</DialogTitle>
        <DialogDescription>{ids?.length > 1 ? `Ces ${ids.length} projets` : 'Ce projet'} et toutes les données associées (bâtiments, lots, fichiers) seront effacés. Cette action est irréversible.</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose()}>Annuler</Button>
        <Button onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">Supprimer définitivement</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
